import React from 'react';
import { getSupabaseClient } from '../supabaseClient';

type BookRow = {
  id: string;
  title: string;
  author: string;
  category: string;
  language: string;
  status: string;
  owner_id: string;
  owner_email: string;
  owner_nickname: string;
  current_holder: string;
  created_at: string;
  updated_at: string;
};

export default function Books() {
  const supabase = getSupabaseClient();
  const [books, setBooks] = React.useState<BookRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const loadBooks = React.useCallback(async () => {
    setLoading(true);
    try {
      // Load books with owner information
      const { data: booksData, error } = await supabase
        .from('books')
        .select(`
          *,
          users!books_owner_id_fkey (
            email,
            nickname
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const booksWithOwner = (booksData || []).map((book: any) => ({
        ...book,
        owner_email: book.users?.email || 'Unknown',
        owner_nickname: book.users?.nickname || 'Unknown',
      }));

      setBooks(booksWithOwner);
    } catch (error) {
      console.error('Failed to load books:', error);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  React.useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const filteredBooks = books.filter(book =>
    book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.owner_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updateBookStatus = async (bookId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('books')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', bookId);

      if (error) throw error;

      // Update local state
      setBooks(prev => prev.map(book =>
        book.id === bookId ? { ...book, status: newStatus } : book
      ));

      alert('Book status updated successfully');
    } catch (error) {
      console.error('Failed to update book status:', error);
      alert('Failed to update book status');
    }
  };

  const deleteBook = async (bookId: string) => {
    if (!confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('books')
        .delete()
        .eq('id', bookId);

      if (error) throw error;

      // Update local state
      setBooks(prev => prev.filter(book => book.id !== bookId));

      alert('Book deleted successfully');
    } catch (error) {
      console.error('Failed to delete book:', error);
      alert('Failed to delete book');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return '#28a745';
      case 'in_transit': return '#ffc107';
      case 'being_read': return '#17a2b8';
      case 'unavailable': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div>
      <h1 className="page-header">
        Book Management
      </h1>
      <div className="page-subtitle">
        Manage books and their availability
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>All Books</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search books..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button onClick={loadBooks}>Refresh</button>
          </div>
        </div>

        {loading ? (
          <div>Loading books...</div>
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <strong>{filteredBooks.length}</strong> book(s) found
              {searchTerm && (
                <span className="muted" style={{ marginLeft: '8px' }}>
                  (filtered from {books.length} total books)
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="table-header-row">
                    <th className="table-cell">Title</th>
                    <th className="table-cell">Author</th>
                    <th className="table-cell">Category</th>
                    <th className="table-cell">Language</th>
                    <th className="table-cell">Status</th>
                    <th className="table-cell">Owner</th>
                    <th className="table-cell">Added</th>
                    <th className="table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBooks.map((book) => (
                    <tr key={book.id} className="table-data-row">
                      <td className="table-cell" style={{ fontWeight: '500' }}>{book.title}</td>
                      <td className="table-cell">{book.author}</td>
                      <td className="table-cell">
                        <span className="category-tag">
                          {book.category}
                        </span>
                      </td>
                      <td className="table-cell">{book.language}</td>
                      <td className="table-cell">
                        <select
                          value={book.status}
                          onChange={(e) => updateBookStatus(book.id, e.target.value)}
                          className="status-select"
                          style={{ background: getStatusColor(book.status) }}
                        >
                          <option value="available">Available</option>
                          <option value="in_transit">In Transit</option>
                          <option value="being_read">Being Read</option>
                          <option value="unavailable">Unavailable</option>
                        </select>
                      </td>
                      <td className="table-cell">
                        <div>
                          <div style={{ fontWeight: '500' }}>{book.owner_nickname}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{book.owner_email}</div>
                        </div>
                      </td>
                      <td className="table-cell">{new Date(book.created_at).toLocaleDateString()}</td>
                      <td className="table-cell">
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => deleteBook(book.id)}
                            className="action-button-small action-button-delete"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Book Statistics</h2>
        <div className="stats-grid">
          <div className="metric-card">
            <div className="metric-value stats-card-value-primary">{books.length}</div>
            <div className="metric-label">Total Books</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-success">
              {books.filter(b => b.status === 'available').length}
            </div>
            <div className="metric-label">Available</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-warning">
              {books.filter(b => b.status === 'in_transit').length}
            </div>
            <div className="metric-label">In Transit</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-info">
              {books.filter(b => b.status === 'being_read').length}
            </div>
            <div className="metric-label">Being Read</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Categories</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {Array.from(new Set(books.map(b => b.category))).map(category => (
            <span
              key={category}
              className="category-tag"
            >
              {category} ({books.filter(b => b.category === category).length})
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}